import { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Shield } from './icons';
import { saveUserAgreements } from '../lib/auth';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  onSuccess: () => void;
}

export function UserAgreementsModal({ isOpen, onClose, userId, onSuccess }: Props) {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agreements, setAgreements] = useState({
    privacyAccepted: false,
    termsAccepted: false,
    disclaimerAccepted: false,
    gdprAccepted: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!userId) {
      setError('No user logged in');
      return;
    }

    if (!Object.values(agreements).every(Boolean)) {
      setError('Please accept all agreements to continue');
      return;
    }

    try {
      setIsLoading(true);
      await saveUserAgreements(userId, agreements);
      onClose();
      onSuccess();
    } catch (err: any) {
      console.error('Error saving agreements:', err);
      setError(err.message || 'Failed to save agreements');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const allAccepted = Object.values(agreements).every(Boolean);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="freud-card-elevated p-8 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-chocolate-900">User Agreements</h2>
            <p className="text-sm text-chocolate-500">Please review and accept to continue</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-coral-100 border border-coral-200 text-coral-700 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-1">
            <label htmlFor="privacyAccepted" className="flex items-start gap-3 p-3 rounded-xl hover:bg-cream-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                id="privacyAccepted"
                checked={agreements.privacyAccepted}
                onChange={(e) => setAgreements(prev => ({
                  ...prev,
                  privacyAccepted: e.target.checked
                }))}
                className="mt-0.5 w-4 h-4 text-sage-600 rounded border-cream-400 focus:ring-sage-500"
              />
              <span className="text-sm text-chocolate-700">
                I have read and agree to the <a href="/privacy" target="_blank" className="text-sage-600 hover:text-sage-700 font-medium underline underline-offset-2">Privacy Policy</a>
              </span>
            </label>

            <label htmlFor="termsAccepted" className="flex items-start gap-3 p-3 rounded-xl hover:bg-cream-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                id="termsAccepted"
                checked={agreements.termsAccepted}
                onChange={(e) => setAgreements(prev => ({
                  ...prev,
                  termsAccepted: e.target.checked
                }))}
                className="mt-0.5 w-4 h-4 text-sage-600 rounded border-cream-400 focus:ring-sage-500"
              />
              <span className="text-sm text-chocolate-700">
                I accept the <a href="/terms" target="_blank" className="text-sage-600 hover:text-sage-700 font-medium underline underline-offset-2">Terms of Service</a>
              </span>
            </label>

            <label htmlFor="disclaimerAccepted" className="flex items-start gap-3 p-3 rounded-xl hover:bg-cream-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                id="disclaimerAccepted"
                checked={agreements.disclaimerAccepted}
                onChange={(e) => setAgreements(prev => ({
                  ...prev,
                  disclaimerAccepted: e.target.checked
                }))}
                className="mt-0.5 w-4 h-4 text-sage-600 rounded border-cream-400 focus:ring-sage-500"
              />
              <span className="text-sm text-chocolate-700">
                I acknowledge the <a href="/disclaimer" target="_blank" className="text-sage-600 hover:text-sage-700 font-medium underline underline-offset-2">Disclaimer & Indemnity</a>
              </span>
            </label>

            <label htmlFor="gdprAccepted" className="flex items-start gap-3 p-3 rounded-xl hover:bg-cream-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                id="gdprAccepted"
                checked={agreements.gdprAccepted}
                onChange={(e) => setAgreements(prev => ({
                  ...prev,
                  gdprAccepted: e.target.checked
                }))}
                className="mt-0.5 w-4 h-4 text-sage-600 rounded border-cream-400 focus:ring-sage-500"
              />
              <span className="text-sm text-chocolate-700">
                I understand and accept how my personal data will be processed according to the <a href="/privacy" target="_blank" className="text-sage-600 hover:text-sage-700 font-medium underline underline-offset-2">GDPR Policy</a>
              </span>
            </label>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isLoading || !allAccepted}
              className="freud-btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : 'Accept & Continue'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
